// FinKloz — Stock Option Register
// Two views: Certificates (per grant) and People (per person).
// Data from Supabase via useOptionGrants hook.

import React, { useState, useMemo, useCallback } from 'react';
import { sans, mono, green, greenLight, greenDark, red, redLight, amber, amberLight, useTheme } from '../../tokens';
import { percentVested, vestedOptions } from '../../services/vestingEngine';
import useOptionGrants from '../../hooks/useOptionGrants';
import OptionDetailPanel from './OptionDetailPanel';
import PersonDetailPanel from './PersonDetailPanel';

const POOL_TOTAL = 0;

function fmt(n) {
  if (!n) return '--';
  return n.toLocaleString('en-CA');
}

function StatusBadge({ pct }) {
  if (pct >= 1) return <span style={{ fontFamily: sans, fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 3, background: greenLight, color: greenDark }}>Fully Vested</span>;
  if (pct > 0) return <span style={{ fontFamily: sans, fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 3, background: amberLight, color: amber }}>{Math.round(pct * 100)}% Vested</span>;
  return <span style={{ fontFamily: sans, fontSize: 9, fontWeight: 600, padding: '2px 6px', borderRadius: 3, background: '#F5F5F7', color: '#86868B' }}>Not Vested</span>;
}

export default function OptionRegister({ companyId }) {
  const t = useTheme();
  const { grants, movements, loading, createGrant, saveGrant, deleteGrant, pauseVesting, resumeVesting, addMovement } = useOptionGrants(companyId);

  const [view, setView] = useState('people'); // 'people' | 'certificates'
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'final' | 'draft'
  const [selectedCohorts, setSelectedCohorts] = useState(new Set());
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedGrant, setSelectedGrant] = useState(null);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [showCohortDropdown, setShowCohortDropdown] = useState(false);
  const [showNewGrant, setShowNewGrant] = useState(false);
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [newGrant, setNewGrant] = useState({
    name: '', rel: 'Employee', grantDate: '', vestStart: '', expiry: '',
    options: 0, price: 0, currency: 'USD', cohort: '', fv: 0, status: 'draft',
  });
  // Draft sub-status per grantId (session-local; persists until page refresh)
  const [draftPhases, setDraftPhases] = useState({}); // grantId → 'draft' | 'pending_approval' | 'ready_to_issue'
  const [newGrantPhase, setNewGrantPhase] = useState('draft');

  const cohorts = useMemo(() => {
    return Array.from(new Set(grants.map(g => g.cohort))).sort();
  }, [grants]);

  const toggleCohort = (c) => {
    setSelectedCohorts(prev => {
      const next = new Set(prev);
      next.has(c) ? next.delete(c) : next.add(c);
      return next;
    });
  };

  const handleSort = (col) => {
    if (sortBy === col) {
      if (sortDir === 'asc') setSortDir('desc');
      else { setSortBy('name'); setSortDir('asc'); } // reset on third click
    } else { setSortBy(col); setSortDir('asc'); }
  };

  // Filtered + enriched grant list
  const filtered = useMemo(() => {
    const rows = grants.filter(g => {
      if (statusFilter !== 'all' && (g.status || 'final') !== statusFilter) return false;
      if (selectedCohorts.size > 0 && !selectedCohorts.has(g.cohort)) return false;
      if (filter && !g.name.toLowerCase().includes(filter.toLowerCase())) return false;
      return true;
    }).map(g => ({
      ...g,
      pctVested: percentVested(g.vestStart, asOfDate),
      numVested: vestedOptions(g.options, g.vestStart, asOfDate),
    }));
    rows.sort((a, b) => {
      let va = a[sortBy], vb = b[sortBy];
      if (typeof va === 'string') { va = va.toLowerCase(); vb = (vb || '').toLowerCase(); }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  }, [grants, filter, statusFilter, selectedCohorts, asOfDate, sortBy, sortDir]);

  const [peopleSortBy, setPeopleSortBy] = useState('name');
  const [peopleSortDir, setPeopleSortDir] = useState('asc');

  const handlePeopleSort = (col) => {
    if (peopleSortBy === col) {
      if (peopleSortDir === 'asc') setPeopleSortDir('desc');
      else { setPeopleSortBy('name'); setPeopleSortDir('asc'); } // reset on third click
    } else { setPeopleSortBy(col); setPeopleSortDir('asc'); }
  };

  // People view — grouped by name
  const people = useMemo(() => {
    const map = {};
    filtered.forEach(g => {
      if (!map[g.name]) map[g.name] = { name: g.name, rel: g.rel, grants: [], totalOptions: 0, totalVested: 0, totalUnvested: 0 };
      map[g.name].grants.push(g);
      map[g.name].totalOptions += g.options;
      map[g.name].totalVested += g.numVested;
      map[g.name].totalUnvested += g.options - g.numVested;
    });
    const rows = Object.values(map);
    rows.sort((a, b) => {
      let va = a[peopleSortBy], vb = b[peopleSortBy];
      if (typeof va === 'string') { va = va.toLowerCase(); vb = (vb || '').toLowerCase(); }
      if (va < vb) return peopleSortDir === 'asc' ? -1 : 1;
      if (va > vb) return peopleSortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return rows;
  }, [filtered, peopleSortBy, peopleSortDir]);

  const totalOptions = filtered.reduce((s, g) => s + g.options, 0);
  const totalVested = filtered.reduce((s, g) => s + g.numVested, 0);
  // Pool calculations use ALL grants (not filtered)
  const allOptions = grants.reduce((s, g) => s + g.options, 0);
  const draftOptions = grants.filter(g => g.status === 'draft').reduce((s, g) => s + g.options, 0);
  const finalOptions = allOptions - draftOptions;
  const poolRemaining = POOL_TOTAL - allOptions;
  const poolUsedPct = Math.round((finalOptions / POOL_TOTAL) * 100);
  const poolDraftPct = Math.round((draftOptions / POOL_TOTAL) * 100);
  const poolRemainingPct = 100 - poolUsedPct - poolDraftPct;

  // KPI computations
  const kpis = useMemo(() => {
    const now = new Date(asOfDate);
    const in12mo = new Date(asOfDate);
    in12mo.setFullYear(in12mo.getFullYear() + 1);

    // Upcoming cliffs (grants where cliff hasn't happened yet — vestStart + 1 year > asOfDate)
    const upcomingCliffs = grants.filter(g => {
      const cliff = new Date(g.vestStart);
      cliff.setFullYear(cliff.getFullYear() + 1);
      return cliff > now && cliff <= in12mo;
    }).length;

    // Options expiring within 12 months
    const expiringOptions = grants.filter(g => {
      if (!g.expiry) return false;
      const exp = new Date(g.expiry);
      return exp > now && exp <= in12mo;
    }).reduce((s, g) => s + g.options, 0);

    // Weighted average fair value
    const totalFV = grants.reduce((s, g) => s + g.fv * g.options, 0);
    const avgFV = grants.length > 0 ? totalFV / totalOptions : 0;

    return { upcomingCliffs, expiringOptions, avgFV };
  }, [grants, totalOptions, asOfDate]);

  const handleCreateGrant = async () => {
    const result = await createGrant({
      ...newGrant,
      expiry: newGrant.expiry || undefined,
    });
    if (result) {
      if (newGrant.status === 'draft') {
        setDraftPhases(p => ({ ...p, [result.id]: newGrantPhase }));
      }
      setShowNewGrant(false);
      setNewGrant({ name: '', rel: 'Employee', grantDate: '', vestStart: '', expiry: '', options: 0, price: 0, currency: 'USD', cohort: '', fv: 0, status: 'draft' });
      setNewGrantPhase('draft');
    }
  };

  const handleSaveGrant = async (updated) => {
    if (!updated?.id) return;
    await saveGrant(updated.id, updated);
    setSelectedGrant(null);
  };

  const handleDocChange = async (grantId, docs) => {
    await saveGrant(grantId, { documents: docs });
  };

  const exportCSV = useCallback(() => {
    const headers = ['Employee', 'Relationship', 'Grant Date', 'Vesting Start', 'Expiry', '# Options', 'Price', 'Currency', 'Cohort', 'Fair Value', 'Vested', '% Vested'];
    const csvRows = [headers.join(',')];
    filtered.forEach(g => {
      csvRows.push([`"${g.name}"`, g.rel, g.grantDate, g.vestStart, g.expiry, g.options, g.price, g.currency, g.cohort, g.fv, g.numVested, `${Math.round(g.pctVested * 100)}%`].join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'stock-options-export.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, [filtered]);

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', fontFamily: sans, color: t.textMuted }}>Loading stock options...</div>;
  }

  const viewBtn = (key, label) => (
    <button onClick={() => setView(key)} style={{
      fontFamily: sans, fontSize: 11, fontWeight: view === key ? 600 : 400,
      padding: '4px 12px', borderRadius: 12,
      border: `1px solid ${view === key ? green : t.border}`,
      background: view === key ? greenLight : 'transparent',
      color: view === key ? greenDark : t.textSecondary,
      cursor: 'pointer',
    }}>{label}</button>
  );

  return (
    <div>
      {/* KPI Strip + Pool Gauge */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        {[
          { label: 'Granted', value: fmt(finalOptions), color: t.textPrimary },
          { label: 'Vested', value: fmt(totalVested), color: green },
        ].map(k => (
          <div key={k.label} style={{ flex: 1, padding: '10px 12px', background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 8 }}>
            <div style={{ fontFamily: sans, fontSize: 8, textTransform: 'uppercase', letterSpacing: 0.5, color: t.textMuted, marginBottom: 4 }}>{k.label}</div>
            <div style={{ fontFamily: mono, fontSize: 16, fontWeight: 700, color: k.color }}>{k.value}</div>
          </div>
        ))}
        {draftOptions > 0 && (
          <div style={{ flex: 1, padding: '10px 12px', background: t.bgCard, border: `1px solid ${amber}`, borderRadius: 8 }}>
            <div style={{ fontFamily: sans, fontSize: 8, textTransform: 'uppercase', letterSpacing: 0.5, color: amber, marginBottom: 4 }}>Committed</div>
            <div style={{ fontFamily: mono, fontSize: 16, fontWeight: 700, color: amber }}>{fmt(draftOptions)}</div>
            <div style={{ fontFamily: sans, fontSize: 9, color: t.textMuted, marginTop: 2 }}>drafts pending</div>
          </div>
        )}
        {/* Pool gauge */}
        <div style={{ flex: 2, padding: '10px 12px', background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
            <span style={{ fontFamily: sans, fontSize: 8, textTransform: 'uppercase', letterSpacing: 0.5, color: t.textMuted }}>Option Pool</span>
            <span style={{ fontFamily: mono, fontSize: 14, fontWeight: 700, color: poolRemainingPct <= 20 ? red : green }}>
              {fmt(poolRemaining)} <span style={{ fontSize: 10, fontWeight: 600 }}>({poolRemainingPct}%)</span> <span style={{ fontSize: 9, fontWeight: 400, color: t.textMuted }}>of {fmt(POOL_TOTAL)}</span>
            </span>
          </div>
          <div style={{ height: 10, background: t.borderLight, borderRadius: 5, overflow: 'hidden', display: 'flex' }}>
            <div style={{ height: '100%', width: `${poolUsedPct}%`, background: green, transition: 'width 0.3s' }} />
            {draftOptions > 0 && <div style={{ height: '100%', width: `${poolDraftPct}%`, background: amber, transition: 'width 0.3s' }} />}
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 5 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: mono, fontSize: 8, color: t.textMuted }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: green, display: 'inline-block' }} />
              {fmt(finalOptions)} granted
            </span>
            {draftOptions > 0 && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: mono, fontSize: 8, color: amber, fontWeight: 600 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: amber, display: 'inline-block' }} />
                {fmt(draftOptions)} committed
              </span>
            )}
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: mono, fontSize: 8, color: t.textMuted }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: t.borderLight, display: 'inline-block' }} />
              {fmt(poolRemaining)} available
            </span>
          </div>
          {poolRemainingPct <= 20 && (
            <div style={{ marginTop: 5, fontFamily: sans, fontSize: 9, color: red, fontWeight: 600, padding: '2px 6px', background: redLight, borderRadius: 4, textAlign: 'center' }}>
              {poolRemainingPct}% available — consider requesting board approval for expansion
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* View toggle */}
        <div style={{ display: 'flex', gap: 4 }}>
          {viewBtn('people', 'People')}
          {viewBtn('certificates', 'Certificates')}
        </div>
        <input type="text" placeholder="Search employee..." value={filter} onChange={e => setFilter(e.target.value)}
          style={{ fontFamily: sans, fontSize: 12, padding: '6px 12px', border: `1px solid ${t.border}`, borderRadius: 6, background: t.bg, color: t.textPrimary, width: 180 }} />
        {/* Cohort multi-select */}
        <div style={{ position: 'relative' }}>
          <button onClick={() => setShowCohortDropdown(!showCohortDropdown)} style={{
            fontFamily: sans, fontSize: 12, padding: '6px 10px', borderRadius: 6,
            border: `1px solid ${t.border}`, background: t.bg, color: t.textPrimary, cursor: 'pointer', minWidth: 120,
          }}>
            {selectedCohorts.size === 0 ? 'All Cohorts' : `${selectedCohorts.size} selected`} &#9662;
          </button>
          {showCohortDropdown && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, zIndex: 10, marginTop: 4,
              background: t.bgCard || '#fff', border: `1px solid ${t.border}`, borderRadius: 6,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)', padding: 6, minWidth: 140,
            }}>
              {cohorts.map(c => (
                <label key={c} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px', cursor: 'pointer', fontFamily: sans, fontSize: 11, color: t.textPrimary }}>
                  <input type="checkbox" checked={selectedCohorts.has(c)} onChange={() => toggleCohort(c)} />
                  {c}
                </label>
              ))}
              <div style={{ borderTop: `1px solid ${t.border}`, marginTop: 4, paddingTop: 4 }}>
                <button onClick={() => { setSelectedCohorts(new Set()); setShowCohortDropdown(false); }}
                  style={{ fontFamily: sans, fontSize: 10, color: t.textMuted, background: 'none', border: 'none', cursor: 'pointer' }}>Clear all</button>
              </div>
            </div>
          )}
        </div>
        {/* Status filter */}
        <div style={{ display: 'flex', borderRadius: 4, overflow: 'hidden', border: `1px solid ${t.border}` }}>
          {[{ key: 'all', label: 'All' }, { key: 'final', label: 'Approved' }, { key: 'draft', label: 'Drafts' }].map(s => (
            <button key={s.key} onClick={() => setStatusFilter(s.key)} style={{
              fontFamily: sans, fontSize: 10, fontWeight: 600, padding: '4px 10px', border: 'none', cursor: 'pointer',
              background: statusFilter === s.key ? (s.key === 'draft' ? amber : green) : t.bg,
              color: statusFilter === s.key ? '#fff' : t.textSecondary,
            }}>{s.label}</button>
          ))}
        </div>
        {/* As-of date picker */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{ fontFamily: sans, fontSize: 10, color: t.textMuted }}>As of:</span>
          <input type="date" value={asOfDate} onChange={e => setAsOfDate(e.target.value)}
            style={{ fontFamily: mono, fontSize: 11, padding: '4px 6px', border: `1px solid ${t.border}`, borderRadius: 4, background: t.bg }} />
        </div>
        <div style={{ marginLeft: 'auto', fontFamily: sans, fontSize: 11, color: t.textSecondary }}>
          {view === 'people' ? `${people.length} people` : `${filtered.length} grants`} | {fmt(totalOptions)} options{draftOptions > 0 ? ` (${fmt(draftOptions)} committed)` : ''}
        </div>
        <button onClick={() => {
          const input = document.createElement('input');
          input.type = 'file';
          input.accept = '.csv';
          input.onchange = async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const text = await file.text();
            const lines = text.split('\n').filter(l => l.trim());
            if (lines.length < 2) return;
            const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
            let imported = 0;
            for (let i = 1; i < lines.length; i++) {
              const vals = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
              const get = (key) => vals[headers.indexOf(key)] || '';
              const name = get('employee') || get('name');
              const opts = parseInt(get('options') || get('# options')) || 0;
              if (!name || !opts) continue;
              await createGrant({
                name, rel: get('relationship') || 'Employee',
                grantDate: get('grant date') || get('grantdate'),
                vestStart: get('vesting start') || get('veststart') || get('grant date') || get('grantdate'),
                options: opts,
                price: parseFloat(get('price') || get('exercise price')) || 0,
                currency: get('currency') || get('ccy') || 'USD',
                cohort: get('cohort') || '',
                fv: parseFloat(get('fair value') || get('fv')) || 0,
              });
              imported++;
            }
            alert(`Imported ${imported} grants from ${file.name}`);
          };
          input.click();
        }} style={{
          fontFamily: sans, fontSize: 11, padding: '5px 12px', borderRadius: 6,
          border: `1px solid ${amber}`, background: amberLight, color: amber, cursor: 'pointer',
        }}>Import CSV</button>
        <button onClick={exportCSV} style={{
          fontFamily: sans, fontSize: 11, padding: '5px 12px', borderRadius: 6,
          border: `1px solid ${green}`, background: greenLight, color: greenDark, cursor: 'pointer',
        }}>Export CSV</button>
        <button onClick={() => setShowNewGrant(true)} style={{
          fontFamily: sans, fontSize: 12, fontWeight: 600, padding: '6px 14px',
          borderRadius: 6, border: `1px solid ${green}`, background: green, color: '#fff', cursor: 'pointer',
        }}>+ New Grant</button>
      </div>

      {/* ─── CERTIFICATES VIEW ─────────────────────────────────────────── */}
      {view === 'certificates' && (
        <>
          {/* Table header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '20px minmax(140px,2fr) minmax(60px,1fr) minmax(70px,1fr) minmax(70px,1fr) minmax(60px,1fr) minmax(50px,0.7fr) minmax(40px,0.6fr) minmax(60px,0.8fr) minmax(70px,1fr) minmax(70px,1fr)',
            gap: 4, padding: '8px 8px', background: green, borderRadius: '6px 6px 0 0',
            fontFamily: sans, fontSize: 9, fontWeight: 600, color: '#fff',
          }}>
            <span></span>
            {[
              { key: 'name', label: 'Employee' },
              { key: 'rel', label: 'Relationship' },
              { key: 'grantDate', label: 'Grant Date' },
              { key: 'vestStart', label: 'Vesting Start' },
              { key: 'options', label: '# Options', align: 'right' },
              { key: 'price', label: 'Price', align: 'right' },
              { key: 'currency', label: 'CCY' },
              { key: 'cohort', label: 'Cohort' },
              { key: 'numVested', label: 'Vested', align: 'right' },
              { key: 'pctVested', label: 'Status', align: 'center' },
            ].map(col => (
              <span key={col.key} onClick={() => handleSort(col.key)}
                style={{ textAlign: col.align || 'left', cursor: 'pointer', userSelect: 'none' }}>
                {col.label} {sortBy === col.key ? (sortDir === 'asc' ? '\u25B2' : '\u25BC') : ''}
              </span>
            ))}
          </div>

          {/* Data rows */}
          {filtered.map((g, i) => {
            const hasDocs = (g.documents || []).length > 0;
            return (
              <div key={g.id} onClick={() => setSelectedGrant(g)} style={{
                display: 'grid',
                gridTemplateColumns: '20px minmax(140px,2fr) minmax(60px,1fr) minmax(70px,1fr) minmax(70px,1fr) minmax(60px,1fr) minmax(50px,0.7fr) minmax(40px,0.6fr) minmax(60px,0.8fr) minmax(70px,1fr) minmax(70px,1fr)',
                gap: 4, padding: '6px 8px',
                background: g.status === 'draft' ? amberLight : (i % 2 === 0 ? '#fff' : '#F5F5F7'),
                borderBottom: `1px solid ${t.borderLight}`,
                borderLeft: g.status === 'draft' ? `3px solid ${amber}` : '3px solid transparent',
                cursor: 'pointer', alignItems: 'center',
              }}>
                <span title={hasDocs ? 'Document attached' : 'No document'} style={{ fontSize: 11, textAlign: 'center', color: hasDocs ? green : t.borderLight }}>
                  {hasDocs ? '\u2B24' : '\u00B7'}
                </span>
                <span style={{ fontFamily: sans, fontSize: 11, color: '#1D1D1F', fontWeight: 500 }}>
                  {g.name}
                  {g.status === 'draft' && (() => {
                    const phase = draftPhases[g.id] || 'draft';
                    const phaseLabel = phase === 'pending_approval' ? 'PENDING APPROVAL' : phase === 'ready_to_issue' ? 'READY TO ISSUE' : 'DRAFT';
                    return <span style={{ fontFamily: sans, fontSize: 8, fontWeight: 700, color: amber, background: '#fff', border: `1px solid ${amber}`, borderRadius: 3, padding: '1px 5px', marginLeft: 6, verticalAlign: 'middle' }}>{phaseLabel}</span>;
                  })()}
                </span>
                <span style={{ fontFamily: sans, fontSize: 9, color: '#86868B' }}>{g.rel}</span>
                <span style={{ fontFamily: mono, fontSize: 10, color: '#1D1D1F' }}>{g.grantDate}</span>
                <span style={{ fontFamily: mono, fontSize: 10, color: '#86868B' }}>{g.vestStart}</span>
                <span style={{ fontFamily: mono, fontSize: 10, color: '#1D1D1F', textAlign: 'right', fontWeight: 600 }}>{fmt(g.options)}</span>
                <span style={{ fontFamily: mono, fontSize: 10, color: '#1D1D1F', textAlign: 'right' }}>${g.price}</span>
                <span style={{ fontFamily: mono, fontSize: 9, color: '#86868B' }}>{g.currency}</span>
                <span style={{ fontFamily: sans, fontSize: 9, color: green }}>{g.cohort}</span>
                <span style={{ fontFamily: mono, fontSize: 10, color: green, textAlign: 'right' }}>{fmt(g.numVested)}</span>
                <span style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                  {g.status === 'draft' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
                      <select
                        value={draftPhases[g.id] || 'draft'}
                        onClick={e => e.stopPropagation()}
                        onChange={e => { e.stopPropagation(); setDraftPhases(p => ({ ...p, [g.id]: e.target.value })); }}
                        style={{ fontFamily: sans, fontSize: 8, padding: '1px 3px', border: `1px solid ${amber}`, borderRadius: 3, background: amberLight, color: amber, cursor: 'pointer', maxWidth: 110 }}>
                        <option value="draft">Draft</option>
                        <option value="pending_approval">Pending Approval</option>
                        <option value="ready_to_issue">Ready to Issue</option>
                      </select>
                      <div style={{ display: 'flex', gap: 3 }}>
                        <button onClick={e => { e.stopPropagation(); saveGrant(g.id, { status: 'final' }); setDraftPhases(p => { const n = { ...p }; delete n[g.id]; return n; }); }} style={{ border: `1px solid ${green}`, background: greenLight, color: greenDark, fontSize: 8, fontFamily: sans, fontWeight: 700, padding: '2px 6px', borderRadius: 3, cursor: 'pointer' }}>Finalize</button>
                        <button onClick={e => { e.stopPropagation(); if (window.confirm(`Discard draft grant for ${g.name}?`)) deleteGrant(g.id); }} style={{ border: `1px solid ${red}`, background: '#FEF2F2', color: red, fontSize: 8, fontFamily: sans, fontWeight: 700, padding: '2px 6px', borderRadius: 3, cursor: 'pointer' }}>Discard</button>
                      </div>
                    </div>
                  ) : <StatusBadge pct={g.pctVested} />}
                </span>
              </div>
            );
          })}

          {/* Totals */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '20px minmax(140px,2fr) minmax(60px,1fr) minmax(70px,1fr) minmax(70px,1fr) minmax(60px,1fr) minmax(50px,0.7fr) minmax(40px,0.6fr) minmax(60px,0.8fr) minmax(70px,1fr) minmax(70px,1fr)',
            gap: 4, padding: '8px 8px', background: green, borderRadius: '0 0 6px 6px',
            fontFamily: mono, fontSize: 11, fontWeight: 700, color: '#fff',
          }}>
            <span></span>
            <span>TOTAL</span>
            <span></span><span></span><span></span>
            <span style={{ textAlign: 'right' }}>{fmt(totalOptions)}</span>
            <span></span><span></span><span></span>
            <span style={{ textAlign: 'right' }}>{fmt(totalVested)}</span>
            <span></span>
          </div>
        </>
      )}

      {/* ─── PEOPLE VIEW ───────────────────────────────────────────────── */}
      {view === 'people' && (
        <>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(160px,2.5fr) minmax(70px,1fr) minmax(50px,0.7fr) minmax(70px,1fr) minmax(70px,1fr) minmax(70px,1fr) minmax(90px,1.2fr)',
            gap: 4, padding: '8px 8px', background: green, borderRadius: '6px 6px 0 0',
            fontFamily: sans, fontSize: 9, fontWeight: 600, color: '#fff',
          }}>
            {[
              { key: 'name', label: 'Employee' },
              { key: 'rel', label: 'Relationship' },
              { key: 'grants', label: 'Grants', align: 'center', sortKey: 'grants' },
              { key: 'totalOptions', label: 'Total Options', align: 'right' },
              { key: 'totalVested', label: 'Vested', align: 'right' },
              { key: 'totalUnvested', label: 'Unvested', align: 'right' },
              { key: 'pct', label: 'Status', align: 'center' },
            ].map(col => {
              const sk = col.sortKey || col.key;
              return (
                <span key={col.key} onClick={() => handlePeopleSort(sk === 'grants' ? 'totalOptions' : sk === 'pct' ? 'totalVested' : sk)}
                  style={{ textAlign: col.align || 'left', cursor: 'pointer', userSelect: 'none' }}>
                  {col.label} {peopleSortBy === (sk === 'grants' ? 'totalOptions' : sk === 'pct' ? 'totalVested' : sk) ? (peopleSortDir === 'asc' ? '\u25B2' : '\u25BC') : ''}
                </span>
              );
            })}
          </div>

          {people.map((p, i) => {
            const pct = p.totalOptions > 0 ? p.totalVested / p.totalOptions : 0;
            return (
              <div key={p.name} onClick={() => setSelectedPerson(p.name)} style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(160px,2.5fr) minmax(70px,1fr) minmax(50px,0.7fr) minmax(70px,1fr) minmax(70px,1fr) minmax(70px,1fr) minmax(90px,1.2fr)',
                gap: 4, padding: '6px 8px',
                background: i % 2 === 0 ? '#fff' : '#F5F5F7',
                borderBottom: `1px solid ${t.borderLight}`,
                cursor: 'pointer', alignItems: 'center',
              }}>
                <span style={{ fontFamily: sans, fontSize: 11, color: '#1D1D1F', fontWeight: 500 }}>{p.name}</span>
                <span style={{ fontFamily: sans, fontSize: 9, color: '#86868B' }}>{p.rel}</span>
                <span style={{ fontFamily: mono, fontSize: 10, color: '#1D1D1F', textAlign: 'center' }}>{p.grants.length}</span>
                <span style={{ fontFamily: mono, fontSize: 10, color: '#1D1D1F', textAlign: 'right', fontWeight: 600 }}>{fmt(p.totalOptions)}</span>
                <span style={{ fontFamily: mono, fontSize: 10, color: green, textAlign: 'right' }}>{fmt(p.totalVested)}</span>
                <span style={{ fontFamily: mono, fontSize: 10, color: amber, textAlign: 'right' }}>{fmt(p.totalOptions - p.totalVested)}</span>
                <span style={{ textAlign: 'center' }}><StatusBadge pct={pct} /></span>
              </div>
            );
          })}

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(160px,2.5fr) minmax(70px,1fr) minmax(50px,0.7fr) minmax(70px,1fr) minmax(70px,1fr) minmax(70px,1fr) minmax(90px,1.2fr)',
            gap: 4, padding: '8px 8px', background: green, borderRadius: '0 0 6px 6px',
            fontFamily: mono, fontSize: 11, fontWeight: 700, color: '#fff',
          }}>
            <span>TOTAL ({people.length} people)</span>
            <span></span><span></span>
            <span style={{ textAlign: 'right' }}>{fmt(totalOptions)}</span>
            <span style={{ textAlign: 'right' }}>{fmt(totalVested)}</span>
            <span style={{ textAlign: 'right' }}>{fmt(totalOptions - totalVested)}</span>
            <span></span>
          </div>
        </>
      )}

      {/* New Grant Modal */}
      {showNewGrant && (
        <>
          <div onClick={() => setShowNewGrant(false)} style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.3)', zIndex: 199,
          }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            background: t.bgCard || '#fff', borderRadius: 12, padding: 24, width: 480,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)', zIndex: 200,
          }}>
            <div style={{ fontFamily: sans, fontSize: 16, fontWeight: 700, color: t.textPrimary, marginBottom: 16 }}>
              New Option Grant
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: newGrant.status === 'draft' ? 8 : 16 }}>
              {['draft', 'final'].map(s => (
                <button key={s} onClick={() => setNewGrant(g => ({ ...g, status: s }))} style={{
                  fontFamily: sans, fontSize: 11, fontWeight: 600, padding: '5px 14px', borderRadius: 4,
                  border: `1px solid ${newGrant.status === s ? (s === 'final' ? green : amber) : t.border}`,
                  background: newGrant.status === s ? (s === 'final' ? greenLight : amberLight) : t.bg,
                  color: newGrant.status === s ? (s === 'final' ? greenDark : amber) : t.textSecondary,
                  cursor: 'pointer',
                }}>
                  {s === 'draft' ? 'Draft' : 'Final (approved)'}
                </button>
              ))}
            </div>
            {newGrant.status === 'draft' && (
              <div style={{ display: 'flex', gap: 6, marginBottom: 16, alignItems: 'center' }}>
                <span style={{ fontFamily: sans, fontSize: 10, color: t.textMuted }}>Phase:</span>
                {[
                  { key: 'draft', label: 'Draft' },
                  { key: 'pending_approval', label: 'Pending Board Approval' },
                  { key: 'ready_to_issue', label: 'Ready to Issue' },
                ].map(p => (
                  <button key={p.key} onClick={() => setNewGrantPhase(p.key)} style={{
                    fontFamily: sans, fontSize: 10, fontWeight: 600, padding: '3px 10px', borderRadius: 4,
                    border: `1px solid ${newGrantPhase === p.key ? amber : t.border}`,
                    background: newGrantPhase === p.key ? amberLight : t.bg,
                    color: newGrantPhase === p.key ? amber : t.textSecondary,
                    cursor: 'pointer',
                  }}>{p.label}</button>
                ))}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[
                { key: 'name', label: 'Employee Name', type: 'text', span: true },
                { key: 'rel', label: 'Relationship', type: 'select', options: ['Employee', 'Consultant', 'Founder', 'Advisor'] },
                { key: 'options', label: '# Options', type: 'number' },
                { key: 'grantDate', label: 'Grant Date', type: 'date' },
                { key: 'vestStart', label: 'Vesting Start', type: 'date' },
                { key: 'price', label: 'Exercise Price', type: 'number' },
                { key: 'currency', label: 'Currency', type: 'select', options: ['USD', 'CAD'] },
                { key: 'cohort', label: 'Cohort', type: 'text' },
              ].map(f => (
                <div key={f.key} style={f.span ? { gridColumn: '1 / -1' } : {}}>
                  <div style={{ fontFamily: sans, fontSize: 10, color: t.textMuted, marginBottom: 2 }}>{f.label}</div>
                  {f.type === 'select' ? (
                    <select value={newGrant[f.key]} onChange={e => setNewGrant(g => ({ ...g, [f.key]: e.target.value }))}
                      style={{ fontFamily: mono, fontSize: 12, width: '100%', padding: '5px 8px', border: `1px solid ${t.border}`, borderRadius: 4, background: t.bg, boxSizing: 'border-box' }}>
                      {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input type={f.type} value={newGrant[f.key] || ''} onChange={e => setNewGrant(g => ({ ...g, [f.key]: f.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value }))}
                      style={{ fontFamily: mono, fontSize: 12, width: '100%', padding: '5px 8px', border: `1px solid ${t.border}`, borderRadius: 4, background: t.bg, boxSizing: 'border-box' }} />
                  )}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button onClick={() => setShowNewGrant(false)} style={{
                fontFamily: sans, fontSize: 12, padding: '6px 14px', borderRadius: 6,
                border: `1px solid ${t.border}`, background: t.bg, color: t.textSecondary, cursor: 'pointer',
              }}>Cancel</button>
              <button onClick={handleCreateGrant} disabled={!newGrant.name || !newGrant.options} style={{
                fontFamily: sans, fontSize: 12, fontWeight: 600, padding: '6px 16px', borderRadius: 6,
                border: `1px solid ${newGrant.name && newGrant.options ? green : t.border}`,
                background: newGrant.name && newGrant.options ? green : t.bgSurface,
                color: newGrant.name && newGrant.options ? '#fff' : t.textMuted,
                cursor: newGrant.name && newGrant.options ? 'pointer' : 'not-allowed',
              }}>
                {newGrant.status === 'draft' ? 'Save as Draft' : 'Create Grant'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Certificate detail panel */}
      {selectedGrant && (
        <>
          <div onClick={() => setSelectedGrant(null)} style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.2)', zIndex: 199,
          }} />
          <OptionDetailPanel
            grant={selectedGrant}
            asOfDate={asOfDate}
            onClose={() => setSelectedGrant(null)}
            onSave={handleSaveGrant}
            onDelete={(id) => { deleteGrant(id); setSelectedGrant(null); }}
            onDocumentsChange={(docs) => handleDocChange(selectedGrant.id, docs)}
          />
        </>
      )}

      {/* Person detail panel */}
      {selectedPerson && (
        <>
          <div onClick={() => setSelectedPerson(null)} style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.2)', zIndex: 199,
          }} />
          <PersonDetailPanel
            personName={selectedPerson}
            grants={grants.filter(g => g.name === selectedPerson)}
            movements={movements}
            asOfDate={asOfDate}
            onClose={() => setSelectedPerson(null)}
            onPauseVesting={pauseVesting}
            onResumeVesting={resumeVesting}
            onAddMovement={addMovement}
            onSelectGrant={(g) => { setSelectedPerson(null); setSelectedGrant(g); }}
          />
        </>
      )}
    </div>
  );
}
